import type {
  NavigationGuardNext,
  RouteLocationNormalized,
  Router as RouterType,
} from "vue-router";
import { useAppStore } from "@/store/modules/app";
import { usePermissionStore } from "@/store/modules/permission";
import { redirectList, whiteList } from "../constants";

// 定义守卫上下文接口
interface GuardContext {
  to: RouteLocationNormalized;
  from: RouteLocationNormalized;
  next: NavigationGuardNext;
  appStore: ReturnType<typeof useAppStore>;
  permissionStore: ReturnType<typeof usePermissionStore>;
  token: string;
}

// 创建上下文
function createContext(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): GuardContext {
  const appStore = useAppStore();
  const permissionStore = usePermissionStore();
  return {
    to,
    from,
    next,
    appStore,
    permissionStore,
    token: appStore.token,
  };
}

// 中间件函数类型
type GuardMiddleware = (context: GuardContext) => boolean | Promise<boolean>;

// 管道函数
async function pipeGuard(
  context: GuardContext,
  middlewares: GuardMiddleware[],
): Promise<void> {
  for (const middleware of middlewares) {
    const shouldContinue = await middleware(context);
    if (!shouldContinue) {
      return;
    }
  }
  context.next();
}

export function createPermissionGuard(router: RouterType) {
  router.beforeEach(async (to, from, next) => {
    const context = createContext(to, from, next);

    // 定义中间件管道
    const middlewares: GuardMiddleware[] = [
      // 白名单检查
      (ctx) => {
        if (whiteList.includes(ctx.to.name as string)) {
          ctx.next();
          return false;
        }
        return true;
      },

      // Token检查
      (ctx) => {
        if (!ctx.token) {
          if (ctx.to.name === "Login") {
            ctx.next();
            return false;
          }
          ctx.appStore?.clear?.();
          ctx.next({ path: "/login" });
          return false;
        }
        return true;
      },

      // Redirect 中转路由直接放行
      (ctx) => {
        if (ctx.to.name === "Redirect") {
          ctx.next();
          return false;
        }
        return true;
      },

      // 登录页限制
      (ctx) => {
        if (ctx.to.name === "Login") {
          ctx.next(ctx.from.path);
          return false;
        }
        return true;
      },

      // 重定向列表检查
      (ctx) => {
        if (redirectList.includes(ctx.to.path)) {
          ctx.next();
          return false;
        }
        return true;
      },

      // 手机绑定检查
      (ctx) => {
        if (!ctx.appStore.userInfo?.accountId) {
          ctx.next({ path: "/bind" });
          return false;
        }
        return true;
      },

      // 菜单生成
      async (ctx) => {
        if (!ctx.permissionStore.menuList.length) {
          await ctx.permissionStore.genMenuList();
        }
        return true;
      },

      // 用户信息完善检查
      (ctx) => {
        const { userDetail } = ctx.permissionStore;
        if (userDetail?.materialType === 0) {
          ctx.next({ path: "/complete" });
          return false;
        }
        return true;
      },

      // 公司权限检查
      (ctx) => {
        const { userRole } = ctx.permissionStore;
        if (!userRole?.companyCode) {
          ctx.next({ path: "/no-access", query: { type: "company" } });
          return false;
        }
        return true;
      },
      // 联盟权限检查
      (ctx) => {
        const { userRole } = ctx.permissionStore;
        if (!userRole?.member) {
          ctx.next({ path: "/no-access", query: { type: "alliance" } });
          return false;
        }
        return true;
      },
    ];

    // 执行中间件管道
    await pipeGuard(context, middlewares);
  });
}
